import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store';
import { createUser, updateUser, fetchUserRoles } from '@/store/slices/usersSlice';
import { User, UserRole } from '@/types';
import { Permission } from '@/utils/permissions';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface UserFormProps {
  user?: User;
  onSuccess: () => void;
  onCancel: () => void;
}

export const UserForm: React.FC<UserFormProps> = ({ user, onSuccess, onCancel }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { roles, isLoading } = useSelector((state: RootState) => state.users);

  const [formData, setFormData] = useState({
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    role: user?.role || UserRole.VIEWER,
    isActive: user?.isActive ?? true,
    additionalPermissions: (user?.additionalPermissions as Permission[] | undefined) || [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privilegeSet, setPrivilegeSet] = useState<Set<Permission>>(
    new Set((user?.additionalPermissions as Permission[] | undefined) || [])
  );

  useEffect(() => {
    if (roles.length === 0) {
      dispatch(fetchUserRoles());
    }
  }, [dispatch, roles.length]);

  useEffect(() => {
    if (user) {
      setPrivilegeSet(new Set((user.additionalPermissions as Permission[] | undefined) || []));
    }
  }, [user]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (user) {
        // Update existing user
        await dispatch(updateUser({
          id: user.id,
          data: formData,
        })).unwrap();
      } else {
        // Create new user
        await dispatch(createUser({
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          additionalPermissions: Array.from(privilegeSet),
        })).unwrap();
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const formatRoleLabel = (role: UserRole) => {
    const roleString = typeof role === 'string' ? role : String(role || '');
    return roleString.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const privilegeOptions: Array<{ key: string; label: string; permissions: Permission[] }> = [
    { key: 'teams', label: 'Add/Update Teams', permissions: [Permission.CREATE_TEAMS, Permission.UPDATE_TEAMS] },
    { key: 'programs', label: 'Add/Update Programs', permissions: [Permission.CREATE_PROGRAMS, Permission.UPDATE_PROGRAMS] },
    { key: 'investibleStructures', label: 'Add/Update Investible Structures', permissions: [Permission.CREATE_INVESTIBLE_STRUCTURES, Permission.UPDATE_INVESTIBLE_STRUCTURES] },
    { key: 'epics', label: 'Add/Update EPICs', permissions: [Permission.CREATE_EPICS, Permission.UPDATE_EPICS] },
    { key: 'features', label: 'Add/Update Features', permissions: [Permission.CREATE_FEATURES, Permission.UPDATE_FEATURES] },
  ];

  const togglePrivilege = (perms: Permission[]) => {
    setPrivilegeSet((prev) => {
      const next = new Set(prev);
      const hasAll = perms.every((p) => next.has(p));
      if (hasAll) {
        perms.forEach((p) => next.delete(p));
      } else {
        perms.forEach((p) => next.add(p));
      }
      setFormData((prevForm) => ({ ...prevForm, additionalPermissions: Array.from(next) }));
      return next;
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Email */}
      <div className="space-y-1.5">
        <label htmlFor="email" className="block text-sm font-medium text-foreground">
          Email Address <span className="text-status-error">*</span>
        </label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          placeholder="user@company.com"
          error={errors.email}
          disabled={isSubmitting}
        />
        {errors.email && (
          <p className="text-xs text-status-error font-medium">{errors.email}</p>
        )}
      </div>

      {/* First Name */}
      <div className="space-y-1.5">
        <label htmlFor="firstName" className="block text-sm font-medium text-foreground">
          First Name <span className="text-status-error">*</span>
        </label>
        <Input
          id="firstName"
          type="text"
          value={formData.firstName}
          onChange={(e) => handleInputChange('firstName', e.target.value)}
          placeholder="John"
          error={errors.firstName}
          disabled={isSubmitting}
        />
        {errors.firstName && (
          <p className="text-xs text-status-error font-medium">{errors.firstName}</p>
        )}
      </div>

      {/* Last Name */}
      <div className="space-y-1.5">
        <label htmlFor="lastName" className="block text-sm font-medium text-foreground">
          Last Name <span className="text-status-error">*</span>
        </label>
        <Input
          id="lastName"
          type="text"
          value={formData.lastName}
          onChange={(e) => handleInputChange('lastName', e.target.value)}
          placeholder="Doe"
          error={errors.lastName}
          disabled={isSubmitting}
        />
        {errors.lastName && (
          <p className="text-xs text-status-error font-medium">{errors.lastName}</p>
        )}
      </div>

      {/* Role */}
      <div className="space-y-1.5">
        <label htmlFor="role" className="block text-sm font-medium text-foreground">
          Role <span className="text-status-error">*</span>
        </label>
        <Select
          value={formData.role}
          onValueChange={(value) => handleInputChange('role', value)}
          options={roles.map(role => ({
            value: role.value,
            label: role.label,
          }))}
          placeholder="Select a role"
          error={errors.role}
          disabled={isSubmitting}
        />
        {errors.role && (
          <p className="text-xs text-status-error font-medium">{errors.role}</p>
        )}
      </div>

      {/* Active Status (only for editing) */}
      {user && (
        <div className="space-y-1.5">
          <label htmlFor="isActive" className="block text-sm font-medium text-foreground">
            Status
          </label>
          <Select
            value={formData.isActive.toString()}
            onValueChange={(value) => handleInputChange('isActive', value === 'true')}
            options={[
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' },
            ]}
            disabled={isSubmitting}
          />
        </div>
      )}

      {/* Role Description */}
      {formData.role && (
        <div className="bg-muted rounded-md p-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground mb-1.5">
            {formatRoleLabel(formData.role)} Permissions
          </h4>
          <p className="text-xs text-muted-foreground">
            {getRoleDescription(formData.role)}
          </p>
        </div>
      )}

      {/* Additional Privileges */}
      <div className="bg-muted rounded-md p-3 space-y-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground">Additional Privileges</h4>
        <p className="text-xs text-muted-foreground">
          Grant specific create/update capabilities beyond the base role.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
          {privilegeOptions.map((option) => {
            const checked = option.permissions.every((p) => privilegeSet.has(p));
            return (
              <label
                key={option.key}
                className="flex items-center space-x-2 bg-background border border-border rounded-md px-3 py-2 cursor-pointer hover:border-primary/50"
              >
                <input
                  type="checkbox"
                  className="rounded text-primary focus:ring-primary"
                  checked={checked}
                  onChange={() => togglePrivilege(option.permissions)}
                  disabled={isSubmitting}
                />
                <span className="text-xs text-foreground">{option.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              {user ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            user ? 'Update User' : 'Create User'
          )}
        </Button>
      </div>
    </form>
  );
};

function getRoleDescription(role: UserRole): string {
  switch (role) {
    case UserRole.EXECUTIVE:
      return 'Portfolio-level view and approvals. Can view all dashboards and approve EPICs.';
    case UserRole.PMO:
      return 'Full planning workflow access. Can manage all planning entities and user accounts.';
    case UserRole.IS_OWNER:
      return 'Manage own Investible Structures, EPICs, and Features. Can create and prioritize work items.';
    case UserRole.PROGRAM_LEAD:
      return 'Manage allocations and delivery status for assigned programs. Can update team assignments.';
    case UserRole.FINANCE:
      return 'Cost profiles and financial data access. Can manage cost profiles and view financial reports.';
    case UserRole.VIEWER:
      return 'Read-only access to assigned entities. Cannot modify any data.';
    default:
      return 'No description available.';
  }
}
